/**
 * screens/MyCash/components/CustodianOnboarding
 * What a custodian sees before anything is assigned to them: three numbered
 * steps in ink circles, explaining what will happen and — critically — that a
 * shortfall reaches payroll only after they have seen it.
 *
 * That last line is the reason this panel exists. Someone whose wages could be
 * docked by a cash count is entitled to know the sequence in advance.
 */
import { StyleSheet, Text, View } from 'react-native';
import { Card, Eyebrow } from '../../../components';
import { neutral } from '../../../theme/colors';
import { radii } from '../../../theme/radii';
import { mono, sans, size as fontSize, leading, type as textRole } from '../../../theme/typography';

const STEPS = [
  {
    n: '1',
    label: 'Finance assigns you cash',
    hint: 'You get a balance and a spending limit.',
  },
  {
    n: '2',
    label: 'You log spends and attach receipts',
    hint: 'Over the limit, it waits for an approver.',
  },
  {
    n: '3',
    label: 'Finance counts what is left',
    hint: 'A shortfall reaches payroll only after you see it.',
  },
] as const;

export function CustodianOnboarding() {
  return (
    <View style={styles.wrap}>
      <Eyebrow>Custodian</Eyebrow>
      <Text style={[textRole.pageTitle, styles.title]}>Nothing is assigned to you yet</Text>
      <Text style={styles.blurb}>
        When finance puts money in your hands, it appears here.
      </Text>

      <Card>
        <View style={styles.steps}>
          {STEPS.map((step) => (
            <View key={step.n} style={styles.step}>
              <View style={styles.circle}>
                <Text style={styles.circleText}>{step.n}</Text>
              </View>
              <View style={styles.stepText}>
                <Text style={styles.stepLabel}>{step.label}</Text>
                <Text style={styles.stepHint}>{step.hint}</Text>
              </View>
            </View>
          ))}
        </View>
      </Card>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: { maxWidth: 520 },
  title: { marginTop: 8, marginBottom: 5 },
  blurb: {
    ...sans('400'),
    fontSize: fontSize.button,
    lineHeight: leading(fontSize.button),
    color: neutral.grey600,
    marginBottom: 20,
  },
  steps: { paddingVertical: 18, paddingHorizontal: 20, gap: 12, backgroundColor: neutral.surface2 },
  step: { flexDirection: 'row', alignItems: 'flex-start', gap: 11 },
  circle: {
    width: 22,
    height: 22,
    borderRadius: radii.numberCircle,
    backgroundColor: neutral.ink,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  circleText: { ...mono('400'), fontSize: fontSize.tableHeader, color: neutral.surface },
  stepText: { flex: 1, minWidth: 0, gap: 1 },
  stepLabel: { ...sans('500'), fontSize: fontSize.button, color: neutral.ink },
  stepHint: {
    ...sans('400'),
    fontSize: fontSize.meta,
    lineHeight: leading(fontSize.meta),
    color: neutral.grey600,
  },
});

export default CustodianOnboarding;
